import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  CalendarMonth as CalendarMonthIcon,
  CallOutlined as CallOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  WorkOutline as WorkOutlineIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/axiosConfig';
import SignedImage from '../common/SignedImage';
import PageContainer from '../common/PageContainer';

const statusesList = ['ACTIVE', 'DISCONTINUED'];
const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const genders = ['MALE', 'FEMALE', 'OTHER'];
const compactSelectSx = {
  minHeight: 40,
  borderRadius: 1.5,
  '& .MuiSelect-select': {
    py: 1,
  },
};

const Surface = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
  boxShadow: '0 22px 54px -34px rgba(15,23,42,0.30)',
  background: '#fff',
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.72rem',
  color: alpha(theme.palette.text.primary, 0.75),
  fontWeight: 700,
  marginBottom: theme.spacing(0.6),
  textTransform: 'uppercase',
  letterSpacing: 0.2,
}));

const SectionCard = ({ title, subtitle, children }) => (
  <Surface>
    <Box sx={{ p: { xs: 2, md: 2.5 } }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {subtitle}
        </Typography>
      ) : null}
      {children}
    </Box>
  </Surface>
);

const QuickInfo = ({ icon, label, value }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha('#2563eb', 0.10),
        color: '#2563eb',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
        {value || 'Not added'}
      </Typography>
    </Box>
  </Stack>
);

const CreateEmployee = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { employeId, employeeId } = useParams();
  const selectedEmployeeId = employeeId || employeId;
  const schoolId = localStorage.getItem('schoolId');

  const [employeeData, setEmployeeData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    qualification: '',
    experience: '',
    subjectsTaught: '',
    emergencyContact: '',
    notes: '',
    role: 'TEACHER',
    dateOfJoining: '',
    salary: '',
    bloodGroup: '',
    status: 'ACTIVE',
    schoolId,
    imageUrl: '',
  });
  const [roles, setRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [completionPercentage, setCompletionPercentage] = useState(0);

  const mandatoryFields = useMemo(
    () => ['firstName', 'phone', 'role', 'dateOfJoining', 'status'],
    []
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDateForInput = (date) => {
    const d = new Date(date);
    const offset = d.getTimezoneOffset();
    const adjustedDate = new Date(d.getTime() - offset * 60 * 1000);
    return adjustedDate.toISOString().split('T')[0];
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get('/api/roles');
        setRoles(Array.isArray(response.data) ? response.data : []);
      } catch {
        showSnackbar('Failed to fetch roles.', 'error');
      }
    };

    const fetchEmployee = async () => {
      if (!selectedEmployeeId) return;
      try {
        const response = await axiosInstance.get(`/api/employees/${selectedEmployeeId}`);
        const data = response.data || {};
        setEmployeeData({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          dateOfBirth: data.dateOfBirth ? formatDateForInput(data.dateOfBirth) : '',
          gender: data.gender || '',
          qualification: data.qualification || '',
          experience: data.experience || '',
          subjectsTaught: data.subjectsTaught || '',
          emergencyContact: data.emergencyContact || '',
          notes: data.notes || '',
          role: data.role || 'TEACHER',
          dateOfJoining: data.dateOfJoining ? formatDateForInput(data.dateOfJoining) : '',
          salary: data.salary || '',
          bloodGroup: data.bloodGroup || '',
          status: data.status && data.status.toUpperCase() === 'ACTIVE' ? 'ACTIVE' : 'DISCONTINUED',
          schoolId: data.schoolId || schoolId,
          imageUrl: data.imageUrl || '',
        });
        if (data.imageUrl) setUploadPreview(data.imageUrl);
      } catch {
        showSnackbar('Failed to fetch employee details.', 'error');
      }
    };

    fetchRoles();
    fetchEmployee();
  }, [selectedEmployeeId, schoolId]);

  useEffect(() => {
    const filled = mandatoryFields.filter((field) => employeeData[field] && employeeData[field].toString().trim() !== '').length;
    setCompletionPercentage(Math.round((filled / mandatoryFields.length) * 100));
  }, [employeeData, mandatoryFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployeeData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setUploadPreview(URL.createObjectURL(file));

    if (!selectedEmployeeId) return;
    const formData = new FormData();
    formData.append('image', file);
    formData.append('employeeId', selectedEmployeeId);
    setUploading(true);
    try {
      const response = await axiosInstance.post('/api/images/profile/employee/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEmployeeData((prev) => ({ ...prev, imageUrl: response.data }));
      showSnackbar('Image uploaded successfully!');
    } catch {
      showSnackbar('Image upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    const temp = {};
    temp.firstName = employeeData.firstName ? '' : 'First Name is required.';
    temp.phone = /^\d{10}$/.test(employeeData.phone || '') ? '' : 'Phone number must be 10 digits.';
    temp.role = employeeData.role ? '' : 'Role is required.';
    temp.dateOfJoining = employeeData.dateOfJoining ? '' : 'Date of Joining is required.';
    temp.status = employeeData.status ? '' : 'Status is required.';
    setErrors(temp);
    return Object.values(temp).every((err) => err === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar('Please fix the errors in the form.', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      const url = `/api/employees${selectedEmployeeId ? `/${selectedEmployeeId}` : ''}`;
      const method = selectedEmployeeId ? 'put' : 'post';
      const response = await axiosInstance({ method, url, data: employeeData, headers: { 'Content-Type': 'application/json' } });
      const newEmployeeId = selectedEmployeeId || response.data?.employeeId;

      if (!selectedEmployeeId && imageFile && newEmployeeId) {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('employeeId', newEmployeeId);
        setUploading(true);
        try {
          await axiosInstance.post('/api/images/profile/employee/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          showSnackbar('Employee saved but image upload failed.', 'warning');
        } finally {
          setUploading(false);
        }
      }

      showSnackbar(`Employee ${selectedEmployeeId ? 'updated' : 'created'} successfully!`);
      navigate(`/employee/${newEmployeeId}`);
    } catch {
      showSnackbar('An error occurred while saving employee.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorList = Object.values(errors).filter(Boolean);
  const pageTitle = selectedEmployeeId ? 'Edit Employee' : 'Create Employee';
  const pageSubtitle = selectedEmployeeId
    ? 'Update staff profile, role, and contact details in a cleaner workspace.'
    : 'Create a new employee profile with the school details that matter first.';

  return (
    <PageContainer>
      <Box sx={{ maxWidth: 1120, width: '100%', mx: 'auto' }}>
        <Surface sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={selectedEmployeeId ? 'Employee Update' : 'New Employee'}
                    sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.10), color: theme.palette.primary.dark }}
                  />
                  <Chip
                    size="small"
                    label={`Completion ${completionPercentage}%`}
                    sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.10), color: theme.palette.success.dark }}
                  />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a' }}>
                  {pageTitle}
                </Typography>
                <Typography sx={{ color: 'text.secondary', maxWidth: 760 }}>
                  {pageSubtitle}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 10,
                  borderRadius: 6,
                  backgroundColor: alpha(theme.palette.divider, 0.35),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    backgroundColor:
                      completionPercentage < 50
                        ? theme.palette.error.main
                        : completionPercentage < 80
                          ? theme.palette.warning.main
                          : theme.palette.success.main,
                  },
                }}
              />
              <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: 'text.secondary' }}>
                Required fields completed
              </Typography>
            </Grid>
          </Grid>
        </Surface>

        {errorList.length > 0 && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {errorList.map((err, idx) => <div key={idx}>{err}</div>)}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                <SectionCard title="Personal Details" subtitle="Core staff identity and communication details.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>First Name</FieldLabel>
                      <TextField size="small" name="firstName" value={employeeData.firstName} onChange={handleChange} required fullWidth error={Boolean(errors.firstName)} helperText={errors.firstName} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Last Name</FieldLabel>
                      <TextField size="small" name="lastName" value={employeeData.lastName} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Gender</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="gender" value={employeeData.gender} onChange={handleChange} displayEmpty sx={compactSelectSx}>
                          <MenuItem value=""><em>Select gender</em></MenuItem>
                          {genders.map((gender) => <MenuItem key={gender} value={gender}>{gender}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Date of Birth</FieldLabel>
                      <TextField size="small" name="dateOfBirth" type="date" value={employeeData.dateOfBirth} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Phone</FieldLabel>
                      <TextField size="small" name="phone" type="tel" value={employeeData.phone} onChange={handleChange} required fullWidth error={Boolean(errors.phone)} helperText={errors.phone} inputProps={{ maxLength: 10, pattern: '[0-9]*' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Email</FieldLabel>
                      <TextField size="small" name="email" type="email" value={employeeData.email} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Emergency Contact</FieldLabel>
                      <TextField size="small" name="emergencyContact" type="tel" value={employeeData.emergencyContact} onChange={handleChange} fullWidth inputProps={{ maxLength: 10, pattern: '[0-9]*' }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Address</FieldLabel>
                      <TextField size="small" name="address" value={employeeData.address} onChange={handleChange} fullWidth />
                    </Grid>
                  </Grid>
                </SectionCard>

                <SectionCard title="Work Details" subtitle="Role, joining date, and academic or work-related information.">
                  <Grid container spacing={1.4}>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Qualification</FieldLabel>
                      <TextField size="small" name="qualification" value={employeeData.qualification} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Experience (Years)</FieldLabel>
                      <TextField size="small" name="experience" type="number" value={employeeData.experience} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Subjects Taught</FieldLabel>
                      <TextField size="small" name="subjectsTaught" value={employeeData.subjectsTaught} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Date of Joining</FieldLabel>
                      <TextField size="small" name="dateOfJoining" type="date" value={employeeData.dateOfJoining} onChange={handleChange} required fullWidth InputLabelProps={{ shrink: true }} error={Boolean(errors.dateOfJoining)} helperText={errors.dateOfJoining} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Role</FieldLabel>
                      <FormControl fullWidth required error={Boolean(errors.role)}>
                        <Select size="small" name="role" value={employeeData.role} onChange={handleChange} sx={compactSelectSx}>
                          {(roles.length ? roles : ['TEACHER', 'EMPLOYEE', 'ADMIN']).map((roleItem) => {
                            const value = typeof roleItem === 'string' ? roleItem : roleItem?.name || roleItem?.roleName || '';
                            return <MenuItem key={value} value={value}>{value}</MenuItem>;
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Status</FieldLabel>
                      <FormControl fullWidth required error={Boolean(errors.status)}>
                        <Select size="small" name="status" value={employeeData.status} onChange={handleChange} sx={compactSelectSx}>
                          {statusesList.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Blood Group</FieldLabel>
                      <FormControl fullWidth>
                        <Select size="small" name="bloodGroup" value={employeeData.bloodGroup} onChange={handleChange} displayEmpty sx={compactSelectSx}>
                          <MenuItem value=""><em>Select blood group</em></MenuItem>
                          {bloodGroups.map((group) => <MenuItem key={group} value={group}>{group}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Salary</FieldLabel>
                      <TextField size="small" name="salary" type="number" value={employeeData.salary} onChange={handleChange} fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <FieldLabel>Additional Notes</FieldLabel>
                      <TextField size="small" name="notes" value={employeeData.notes} onChange={handleChange} fullWidth multiline rows={3} />
                    </Grid>
                  </Grid>
                </SectionCard>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <SectionCard title="Photo" subtitle="Profile image shown across employee screens.">
                  <Box
                    sx={{
                      width: 180,
                      maxWidth: '100%',
                      aspectRatio: '35 / 45',
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      backgroundColor: '#f8fafc',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      mb: 1.5,
                      mx: 'auto',
                    }}
                  >
                    {uploadPreview || employeeData.imageUrl ? (
                      <SignedImage
                        src={uploadPreview || employeeData.imageUrl}
                        alt="Employee"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <PersonIcon sx={{ fontSize: 88, color: alpha(theme.palette.text.primary, 0.34) }} />
                    )}
                  </Box>
                  <Button variant="outlined" component="label" fullWidth disabled={uploading} sx={{ textTransform: 'none', borderRadius: 3 }}>
                    {uploading ? <CircularProgress size={18} /> : 'Upload / Change Photo'}
                    <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                  </Button>
                </SectionCard>

                <SectionCard title="Quick Summary" subtitle="Helpful context while editing the employee record.">
                  <Stack spacing={1.5}>
                    <QuickInfo icon={<CallOutlinedIcon sx={{ fontSize: 18 }} />} label="Phone" value={employeeData.phone} />
                    <QuickInfo icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />} label="Email" value={employeeData.email} />
                    <QuickInfo icon={<WorkOutlineIcon sx={{ fontSize: 18 }} />} label="Role" value={employeeData.role} />
                    <QuickInfo icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />} label="Joining Date" value={employeeData.dateOfJoining} />
                  </Stack>
                </SectionCard>

                <SectionCard title="Actions" subtitle="Save the employee record or return to the employee list.">
                  <Stack spacing={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      disabled={isSubmitting}
                      sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 700, boxShadow: 'none' }}
                    >
                      {isSubmitting ? (selectedEmployeeId ? 'Updating...' : 'Saving...') : (selectedEmployeeId ? 'Update Employee' : 'Save Employee')}
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={() => navigate('/employees')} disabled={isSubmitting} sx={{ textTransform: 'none', borderRadius: 3 }}>
                      Cancel
                    </Button>
                  </Stack>
                </SectionCard>
              </Stack>
            </Grid>
          </Grid>
        </form>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </PageContainer>
  );
};

export default CreateEmployee;
